import React from 'react';

import { debounce, isEqual } from 'lodash-es';
import { connect } from 'react-redux';

import { BranchingModelState } from 'src/redux/branches/reducers/branch-list-reducer';
import { Ref as BranchRef } from 'src/sections/create-branch';
import { Ref } from 'src/sections/repository/types';
import {
  getRepositoryMainBranch,
  getRepositoryBranchingModel,
} from 'src/selectors/repository-selectors';
import {
  getCurrentUser,
  getCurrentUserUuid,
} from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';

import Filter from '../components/filter';
import { Filter as FilterType, PRFilterState } from '../types';

export const FILTER_DEBOUNCE_INTERVAL = 250;

type Props = {
  repositoryFullSlug: string;
  filter: FilterType;
  onFilterChanged: (props: FilterType) => void;
  showIsReviewing: boolean;
  mainBranch?: Ref;
  branchingModel?: BranchingModelState;
  currentUserUuid: string;
};
type State = FilterType & {
  lastFilter: FilterType;
};

export class StatefulPullRequestListFilter extends React.Component<
  Props,
  State
> {
  state = {
    ...this.props.filter,
    // This is used in getDerivedStateFromProps, but not in the component
    lastFilter: this.props.filter,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (!isEqual(props.filter, state.lastFilter)) {
      return {
        ...props.filter,
        lastFilter: props.filter,
      };
    }
    return null;
  }

  notifyFilterChange = () => {
    // strip out lastFilter
    const { lastFilter, ...filter } = this.state;
    this.props.onFilterChanged(filter);
  };

  debouncedNotify = debounce(this.notifyFilterChange, FILTER_DEBOUNCE_INTERVAL);

  onChangeReviewing = (isReviewing: boolean) => {
    this.setState({ isReviewing }, this.notifyFilterChange);
  };

  onChangePrState = (prState: PRFilterState[], statesAllChecked?: boolean) => {
    this.setState(
      {
        prState,
        prStatesAllChecked: statesAllChecked || false,
      },
      this.notifyFilterChange
    );
  };

  onChangeSearch = (search: string) => {
    this.setState({ search: search || undefined }, this.debouncedNotify);
  };

  onChangeUuid = (uuid: string | null) => {
    this.setState({ uuid: uuid || undefined }, this.notifyFilterChange);
  };

  onChangeBranch = (branch: BranchRef | undefined) => {
    this.setState({ branch: branch?.name }, this.notifyFilterChange);
  };

  onChangeSort = (sort: string | undefined) => {
    this.setState({ sort: sort || undefined }, this.notifyFilterChange);
  };

  render() {
    const { repositoryFullSlug, mainBranch, branchingModel, currentUserUuid } =
      this.props;
    const {
      isReviewing,
      search,
      prState,
      prStatesAllChecked,
      uuid,
      branch,
      sort,
    } = this.state;

    return (
      <Filter
        repositoryFullSlug={repositoryFullSlug}
        isReviewing={isReviewing}
        search={search || ''}
        prState={prState}
        prStateAllChecked={prStatesAllChecked}
        uuid={uuid}
        currentUserUuid={currentUserUuid}
        branch={branch}
        sort={sort}
        onChangeReviewing={this.onChangeReviewing}
        onChangePrState={this.onChangePrState}
        onChangeSearch={this.onChangeSearch}
        onChangeUuid={this.onChangeUuid}
        onChangeBranch={this.onChangeBranch}
        onChangeSort={this.onChangeSort}
        showIsReviewing={this.props.showIsReviewing}
        mainBranch={mainBranch}
        branchingModel={branchingModel}
      />
    );
  }
}

const mapStateToProps = (state: BucketState) => ({
  showIsReviewing: !!getCurrentUser(state),
  mainBranch: getRepositoryMainBranch(state),
  branchingModel: getRepositoryBranchingModel(state),
  currentUserUuid: getCurrentUserUuid(state),
});

export default connect(mapStateToProps)(StatefulPullRequestListFilter);
